import { useCallback, useMemo } from "react";
import { Modal } from "shared/components/Modal";
import {
  IconButton,
  IconButtonSize,
  IconButtonVariant
} from "shared/components/IconButton";
import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { instance } from "api/apiBase";
import type { DuplicateContact } from "api/contacts";
import { useAuthContext } from "providers/Auth";

import { Cancel01Icon } from "@hugeicons-pro/core-stroke-rounded";
import {
  CreateLeadPayload,
  useFormController
} from "shared/app/LeadsTableView/components/CreateLeadModal/useFormController";
import { BasicInfoSection } from "shared/app/LeadsTableView/components/CreateLeadModal/sections/BasicInfo";
import { ContactInfoSection } from "shared/app/LeadsTableView/components/CreateLeadModal/sections/ContactInfo";
import { InterestedInSection } from "shared/app/LeadsTableView/components/CreateLeadModal/sections/InterestedIn";
import { ProfileSection } from "shared/app/LeadsTableView/components/CreateLeadModal/sections/Profile";
import { DetailsSection } from "shared/app/LeadsTableView/components/CreateLeadModal/sections/Details";
import { Footer } from "shared/app/LeadsTableView/components/CreateLeadModal/Footer";
import { useDuplicateCheck } from "shared/app/LeadsTableView/components/CreateLeadModal/hooks/useDuplicateCheck";
import { getLeadMutationErrorMessage } from "shared/app/LeadsTableView/utils/leadErrors";
import { LEAD_TYPE_LABEL, LeadType } from "views/leads-id/types";
import { getCreatableLeadTypes } from "views/leads-id/utils/leadTypeRules";

export interface CreateLeadDefaults {
  property?: { id: number; name: string };
  unit?: { id: number; title: string };
  firstName?: string;
  lastName?: string;
  // 10-digit phone (no country code) to pre-populate the primary phone field.
  primaryPhone?: string;
  message?: string;
  // Partner user to pre-select under "Assigned to".
  assigneeId?: number;
  // When converting an AI call transcript, links the new lead to that transcript
  // server-side in the create request (avoids a separate, orphan-prone PATCH).
  callTranscriptId?: number;
  // The same for a teammate call (crm_calls): the call, its recording and
  // transcript land on the new lead. For an inbound call the API also names the
  // dialed number in the Lead created entry and keeps a reused lead's source
  // (CRM-1618).
  callId?: number;
  // Pre-selects the Source by name once the sources load (e.g. "Incoming call"
  // for the in-call lead capture, CRM-905). Ignored if no source matches.
  sourceName?: string;
  // CRM-1618: pre-selects this source instead (the called number's own one);
  // sourceName is the fallback when it isn't among the options.
  sourceId?: number;
}

const TENANT_REALTOR_TYPE_HINT =
  "You can switch between Tenant and Realtor later.";
const OWNER_TYPE_HINT = `${TENANT_REALTOR_TYPE_HINT} Owner can't be changed after the lead is created.`;

const NO_DUPLICATES: DuplicateContact[] = [];

const buildTypeSpecificFields = (
  leadType: LeadType,
  fields: Pick<
    CreateLeadPayload,
    | "property_id"
    | "unit_id"
    | "desired_move_in_date"
    | "message"
    | "portfolio_size"
    | "current_property_manager"
  >
) => {
  if (leadType === LeadType.Owner)
    return {
      portfolio_size: fields.portfolio_size ?? null,
      current_property_manager: fields.current_property_manager || null
    };
  if (leadType === LeadType.Realtor)
    return { property_id: fields.property_id, unit_id: fields.unit_id };
  return {
    property_id: fields.property_id,
    unit_id: fields.unit_id,
    desired_move_in_date: fields.desired_move_in_date,
    message: fields.message
  };
};

export const CreateLeadModal = ({
  open,
  onClose,
  refetchLeads,
  defaults,
  onCreated,
  zIndex,
  requirePropertyAndUnit = false,
  successMessage
}: {
  open: boolean;
  onClose: () => void;
  refetchLeads: () => void;
  defaults?: CreateLeadDefaults;
  // Opt-in for flows that can't use a lead without a unit. Off for the Leads table.
  requirePropertyAndUnit?: boolean;
  // Fires with the created lead after a successful save. Optional so existing
  // callers are unaffected.
  onCreated?: (lead: { id: number }) => void;
  // Lets callers raise the modal above higher-stacking surfaces (e.g. a drawer).
  zIndex?: number;
  // The success toast, worded with the new lead's full name.
  successMessage?: (leadName: string) => string;
}) => {
  const initialValues = useMemo<Partial<CreateLeadPayload> | undefined>(() => {
    if (!defaults) return undefined;
    const values: Partial<CreateLeadPayload> = {
      property_id: defaults.property?.id,
      unit_id: defaults.unit?.id,
      first_name: defaults.firstName,
      last_name: defaults.lastName,
      primary_phone: defaults.primaryPhone,
      message: defaults.message,
      assignee_id: defaults.assigneeId
    };
    const prefilled = Object.fromEntries(
      Object.entries(values).filter(([, value]) => value !== undefined)
    );
    return Object.keys(prefilled).length ? prefilled : undefined;
  }, [defaults]);

  const initialLabels = useMemo(
    () => ({
      property: defaults?.property?.name,
      unit: defaults?.unit?.title
    }),
    [defaults]
  );

  const { type } = useAuthContext();
  // Owner leads can't be tied to a property/unit, so on the Community/Unit page
  // leads tabs (which pre-fill a property/unit) Owner is not offered; Tenant
  // stays the default type.
  const hasEntityContext = Boolean(defaults?.property || defaults?.unit);
  const creatableLeadTypes = useMemo(
    () =>
      getCreatableLeadTypes({
        isTenantLeads: type.isTenantLeads,
        isOwnerLeads: type.isOwnerLeads,
        isShowings: type.isShowings,
        hasEntityContext
      }),
    [type.isTenantLeads, type.isOwnerLeads, type.isShowings, hasEntityContext]
  );
  const leadTypeOptions = useMemo(
    () =>
      creatableLeadTypes.map((leadType) => ({
        label: LEAD_TYPE_LABEL[leadType],
        value: leadType
      })),
    [creatableLeadTypes]
  );
  const defaultLeadType = creatableLeadTypes[0] ?? LeadType.Tenant;

  const form = useFormController({
    initialValues:
      defaultLeadType === LeadType.Tenant
        ? initialValues
        : { ...initialValues, lead_type: defaultLeadType },
    requirePropertyAndUnit
  });
  const leadType = form.values.lead_type ?? LeadType.Tenant;
  const isOwner = leadType === LeadType.Owner;
  const isRealtor = leadType === LeadType.Realtor;
  const {
    duplicates,
    check: checkDuplicates,
    reset: resetDuplicates
  } = useDuplicateCheck();

  const handleContactFieldBlur = useCallback(() => {
    if (isRealtor) return;
    const allEmails = [
      form.values.primary_email ?? "",
      ...(form.values.other_emails ?? [])
    ];
    const allPhones = [
      form.values.primary_phone ?? "",
      ...(form.values.other_phones ?? [])
    ];
    checkDuplicates(allEmails, allPhones);
  }, [
    form.values.primary_email,
    form.values.primary_phone,
    form.values.other_emails,
    form.values.other_phones,
    checkDuplicates,
    isRealtor
  ]);

  const handleClose = () => {
    onClose();
    form.reset();
    resetDuplicates();
  };

  const { mutate: createLead, isPending } = useMutation({
    mutationKey: ["CREATE_LEAD"],
    mutationFn: async (values: CreateLeadPayload) => {
      // Build contact methods; skip empty primaries, add "1" prefix to phones.
      const processedContactMethods = [
        ...(values.primary_phone?.trim()
          ? [
              {
                type: "phone",
                value: `1${values.primary_phone}`,
                is_primary: true
              }
            ]
          : []),
        ...(values.primary_email?.trim()
          ? [
              {
                type: "email",
                value: values.primary_email,
                is_primary: true
              }
            ]
          : []),
        ...values.other_phones
          .filter((phone) => phone.trim() !== "")
          .map((phone) => ({
            type: "phone",
            value: `1${phone}`,
            is_primary: false
          })),
        ...values.other_emails
          .filter((email) => email.trim() !== "")
          .map((email) => ({ type: "email", value: email, is_primary: false }))
      ];

      const {
        property_id,
        unit_id,
        desired_move_in_date,
        message,
        portfolio_size,
        current_property_manager,
        ...rest
      } = values;

      // Owner leads can't be linked to a property/unit and have no move-in or
      // message field; tenant leads don't carry portfolio fields; realtor leads
      // carry only the property/unit. Send only the fields that apply to the
      // selected type so the backend never rejects it.
      const typeSpecificFields = buildTypeSpecificFields(values.lead_type, {
        property_id,
        unit_id,
        desired_move_in_date,
        message,
        portfolio_size,
        current_property_manager
      });

      return instance
        .post<{ id: number }>("/portal/v1/leads", {
          ...rest,
          ...typeSpecificFields,
          contact_methods: processedContactMethods,
          call_transcript_id: defaults?.callTranscriptId,
          call_id: defaults?.callId,
          source_id: Number(values.source_id) || undefined,
          assignee_ids:
            values.assignee_id && Number(values.assignee_id)
              ? [Number(values.assignee_id)]
              : undefined
        })
        .then((res) => res.data);
    },
    onSuccess: (lead, values) => {
      const leadName = `${values.first_name} ${values.last_name}`.trim();
      toast.success(
        successMessage ? successMessage(leadName) : "Lead created successfully"
      );
      refetchLeads();
      onCreated?.(lead);
      handleClose();
    },
    onError: (error) =>
      toast.error(getLeadMutationErrorMessage(error, { isOwnerLead: isOwner }))
  });

  return (
    <Modal
      opened={open}
      onClose={handleClose}
      zIndex={zIndex}
      panelClassName="w-full max-w-[816px] p-0 overflow-y-hidden mx-5"
    >
      <form
        onSubmit={form.onSubmit({
          onSuccess: () => createLead(form.values as CreateLeadPayload)
        })}
        className="flex flex-col max-h-[85vh]"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-alpha-100 bg-white sticky top-0 z-10">
          <div>
            <h3 className="text-xl leading-6 font-bold text-content-1000">
              Create lead
            </h3>
          </div>
          <IconButton
            aria-label="Close"
            variant={IconButtonVariant.TEXT_BASE}
            size={IconButtonSize.SM}
            HugeIcon={Cancel01Icon}
            onClick={handleClose}
          />
        </div>
        {/* Body (scroll area) */}
        <div className="flex-1 overflow-y-auto p-6 flex flex-col gap-6 bg-background-900">
          <BasicInfoSection
            register={form.register}
            showTypeSelect={creatableLeadTypes.length > 1}
            leadType={leadType}
            typeOptions={leadTypeOptions}
            typeHint={
              creatableLeadTypes.includes(LeadType.Owner)
                ? OWNER_TYPE_HINT
                : TENANT_REALTOR_TYPE_HINT
            }
            onLeadTypeChange={(value) => {
              form.handleChange("lead_type", () => value)();
              if (value === LeadType.Realtor) resetDuplicates();
            }}
          />
          <ContactInfoSection
            form={form}
            duplicates={isRealtor ? NO_DUPLICATES : duplicates}
            onContactFieldBlur={handleContactFieldBlur}
          />
          {isOwner ? (
            <ProfileSection form={form} />
          ) : (
            <InterestedInSection
              form={form}
              initialLabels={initialLabels}
              required={requirePropertyAndUnit}
            />
          )}
          <DetailsSection
            form={form}
            leadType={leadType}
            propertyId={defaults?.property?.id}
            defaultSourceName={defaults?.sourceName}
            defaultSourceId={defaults?.sourceId}
          />
        </div>
        <Footer onClose={handleClose} isPending={isPending} />
      </form>
    </Modal>
  );
};
